import React, { useCallback, useMemo, useContext } from 'react';
import FormBody from '../../components/form_body';
import { OccurrenceContext } from '../../../../../providers/OccurrenceProvider';
import type CreateEditOccurrenceUsecase from '../../../../../domain/usecases/create_edit/CreateEditOccurrenceUsecase';
import type CreateEditOccurrenceState from '../../../../../domain/usecases/create_edit/CreateEditOccurrenceState';
import type { CreateEditOccurrenceVictimsForm } from '../../../../../domain/forms/CreateEditOccurrenceVictimsForm';

import { Form, Row, Col } from 'react-bootstrap';
import {
  CustomForm,
  FormItem
} from '../../../../../../../shared/components/custom_form';
import AddItemOnFormButton from '../../../../../../../shared/components/add_item_on_form_button';
import { Divider } from '../../../../../../../shared/styles';
import FormItemCount from '../form_item_count';
import FormItemHeader from '../form_item_header';
import { Field } from 'formik';
import { RefactorSelectWithSearch } from '../../../../../../../components/SelectWithSearch';
import { QualificationType } from '../../../../../data/entities/QualificationEntity';
import { StatusType } from '../../../../../data/entities/StatusEntity';
import { PositionType } from '../../../../../data/entities/PositionEntity';
import { CircumstanceType } from '../../../../../data/entities/CircumstanceEntity';
import { EVictimsTypePerson } from '../../../../../../../shared/enums/victimsTypePerson';
import { convertToDateTimeInput } from '../../../../../../../utils/date';

type VictimFormItemProps = {
  victimForm: CreateEditOccurrenceVictimsForm;
  index: number;
  maxDateTime: string;
  dataCy: (field: string, index?: number) => string;
  enableFieldsByTypeQualification: (
    idsQualification: string[],
    type: QualificationType
  ) => boolean;
  createEditOccurrenceUsecase: CreateEditOccurrenceUsecase;
  fieldsForValidation: CreateEditOccurrenceState['fieldsForValidation'];
  agentQualification: CreateEditOccurrenceState['agentQualification'];
  qualificationsRequestStatus: CreateEditOccurrenceState['qualificationsRequestStatus'];
  ageGroupsRequestStatus: CreateEditOccurrenceState['ageGroupsRequestStatus'];
  genresRequestStatus: CreateEditOccurrenceState['genresRequestStatus'];
  racesRequestStatus: CreateEditOccurrenceState['racesRequestStatus'];
  placesRequestStatus: CreateEditOccurrenceState['placesRequestStatus'];
  circumstancesRequestStatus: CreateEditOccurrenceState['circumstancesRequestStatus'];
  statusRequestStatus: CreateEditOccurrenceState['statusRequestStatus'];
  partiesRequestStatus: CreateEditOccurrenceState['partiesRequestStatus'];
  positionsRequestStatus: CreateEditOccurrenceState['positionsRequestStatus'];
  coorporationsRequestStatus: CreateEditOccurrenceState['coorporationsRequestStatus'];
};

const VictimFormItem: React.FC<VictimFormItemProps> = React.memo(
  ({
    victimForm,
    index,
    maxDateTime,
    dataCy,
    enableFieldsByTypeQualification,
    createEditOccurrenceUsecase,
    fieldsForValidation,
    agentQualification,
    qualificationsRequestStatus,
    ageGroupsRequestStatus,
    genresRequestStatus,
    racesRequestStatus,
    placesRequestStatus,
    circumstancesRequestStatus,
    statusRequestStatus,
    partiesRequestStatus,
    positionsRequestStatus,
    coorporationsRequestStatus
  }) => (
    <div key={victimForm.uniqueKey} data-form-block={`Vítima ${index + 1}`}>
      <Divider height={24} />

      <CustomForm
        validationSchema={victimForm.validationSchema({
          politicalQualificationId:
            fieldsForValidation.politicalQualification?.id,
          agentQualificationId: fieldsForValidation.agentQualification?.id,
          servicesQualificationsIds:
            fieldsForValidation.servicesQualifications?.map(q => q.id)
        })}
        initialValues={victimForm}
        forwardedRef={victimForm.ref}
        onSubmit={() => {}}
        handleFormChange={(field, value) =>
          createEditOccurrenceUsecase.handleVictimsFormChange(
            index,
            field,
            value
          )
        }
        component={({
          values,
          handleChange,
          handleChangeSilent,
          handleSubmit,
          handleBlur,
          isInvalid,
          syncField
        }) => {
          const clearAgentFields = () => {
            handleChange('coorporationId', '');
            handleChange('agentPositionId', '');
            handleChange('agentStatusId', '');
            handleChange('unit', '');
          };

          const clearPoliticalFields = () => {
            handleChange('partieId', '');
            handleChange('politicalPositionId', '');
            handleChange('politicalStatusId', '');
          };

          const clearServiceFields = () => {
            handleChange('serviceStatusId', '');
          };

          function onChangeQualificationField(options: any) {
            handleChange(
              'qualifications',
              options.map((option: any) => option.value)
            );

            const types = options.map((option: any) => option.type);

            if (!types.includes(QualificationType.POLITICAL)) {
              clearPoliticalFields();
            }

            if (!types.includes(QualificationType.AGENT)) {
              clearAgentFields();
              handleChange('personType', EVictimsTypePerson.Civilian);
            }

            if (!types.includes(QualificationType.SERVICE)) {
              clearServiceFields();
            }
          }

          function onChangeAgentRadio(e: any) {
            handleChange('personType', e.target.value);

            handleChange('qualifications', [
              ...values['qualifications'],
              agentQualification.id
            ]);

            clearAgentFields();
          }

          function onChangeAgeField(e: any) {
            const age = e.target.value;
            let ageGroupName = '';
            if (age) {
              if (age >= 0 && age <= 11) {
                ageGroupName = 'criança';
              } else if (age > 11 && age <= 17) {
                ageGroupName = 'adolescente';
              } else if (age > 17 && age <= 59) {
                ageGroupName = 'adulto';
              } else if (age >= 60) {
                ageGroupName = 'idoso';
              }
            }

            const ageGroupId =
              createEditOccurrenceUsecase.getAgeGroupByName(ageGroupName).id;

            const normalizedAge = age < 0 ? '0' : e.target.value;

            handleChangeSilent('ageGroupId', ageGroupId);
            handleChangeSilent('age', normalizedAge);

            createEditOccurrenceUsecase.handleVictimsFormBatchChange(index, {
              ageGroupId,
              age: normalizedAge
            });
          }

          return (
            <Form noValidate onSubmit={handleSubmit}>
              <FormItemHeader
                title="VÍTIMA"
                index={index}
                onDelete={() =>
                  createEditOccurrenceUsecase.removeVictimsForm(index)
                }
                children={
                  <>
                    <Row>
                      <Col md={4}>
                        <Row>
                          <Col>
                            <FormItem
                              name="situation"
                              label="Situação da vítima*"
                              component={field => (
                                <>
                                  <Form.Check>
                                    <Field
                                      as={Form.Check.Input}
                                      type="radio"
                                      id={`${field.name}-${index}-wounded`}
                                      name={field.name}
                                      data-cy={
                                        dataCy(field.name, index) + '-wounded'
                                      }
                                      value="Wounded"
                                      onChange={(e: any) => {
                                        handleChange(
                                          field.name,
                                          e.target.value
                                        );

                                        handleChange('deathDate', '');
                                      }}
                                      isInvalid={isInvalid(field.name)}
                                    />
                                    <Form.Check.Label
                                      htmlFor={`${field.name}-${index}-wounded`}
                                      style={{ color: '#FF9F43' }}
                                    >
                                      Ferido
                                    </Form.Check.Label>
                                  </Form.Check>

                                  <Form.Check inline>
                                    <Field
                                      as={Form.Check.Input}
                                      type="radio"
                                      id={`${field.name}-${index}-dead`}
                                      name={field.name}
                                      data-cy={
                                        dataCy(field.name, index) + '-dead'
                                      }
                                      value="Dead"
                                      onChange={(e: any) =>
                                        handleChange(field.name, e.target.value)
                                      }
                                      isInvalid={isInvalid(field.name)}
                                    />
                                    <Form.Check.Label
                                      htmlFor={`${field.name}-${index}-dead`}
                                      style={{ color: '#EA5455' }}
                                    >
                                      Morto
                                    </Form.Check.Label>
                                  </Form.Check>
                                </>
                              )}
                            />
                          </Col>
                          <Col>
                            <FormItem
                              name="personType"
                              label="Tipo de vítima*"
                              component={field => (
                                <>
                                  <Form.Check>
                                    <Field
                                      as={Form.Check.Input}
                                      type="radio"
                                      id={`${field.name}-${index}-civilian`}
                                      name={field.name}
                                      data-cy={
                                        dataCy(field.name, index) + '-civil'
                                      }
                                      value="Civilian"
                                      onChange={(e: any) => {
                                        handleChange(
                                          field.name,
                                          e.target.value
                                        );

                                        const filterQualifications = values[
                                          'qualifications'
                                        ].filter(
                                          qualification =>
                                            qualification !==
                                            agentQualification.id
                                        );

                                        clearAgentFields();

                                        handleChange(
                                          'qualifications',
                                          filterQualifications
                                        );
                                      }}
                                      isInvalid={isInvalid(field.name)}
                                    />
                                    <Form.Check.Label
                                      htmlFor={`${field.name}-${index}-civilian`}
                                    >
                                      Civil
                                    </Form.Check.Label>
                                  </Form.Check>

                                  <Form.Check inline>
                                    <Field
                                      as={Form.Check.Input}
                                      type="radio"
                                      id={`${field.name}-${index}-agent`}
                                      name={field.name}
                                      data-cy={
                                        dataCy(field.name, index) + '-agent'
                                      }
                                      value="Agent"
                                      onChange={(e: any) =>
                                        onChangeAgentRadio(e)
                                      }
                                      isInvalid={isInvalid(field.name)}
                                    />
                                    <Form.Check.Label
                                      htmlFor={`${field.name}-${index}-agent`}
                                    >
                                      Agente
                                    </Form.Check.Label>
                                  </Form.Check>
                                </>
                              )}
                            />
                          </Col>
                        </Row>
                      </Col>

                      <Col md={6}>
                        <FormItem
                          name="qualifications"
                          label="Qualificações*"
                          failRequest={qualificationsRequestStatus.maybeMap({
                            failed: () => true
                          })}
                          onPressedRefresh={() =>
                            createEditOccurrenceUsecase.getQualifications()
                          }
                          component={field => (
                            <RefactorSelectWithSearch
                              dataCy={dataCy(field.name, index)}
                              options={qualificationsRequestStatus.maybeMap({
                                idle: () => [],
                                loading: () => [],
                                failed: () => [],
                                succeeded(data) {
                                  const qualifications = data.filter(
                                    qualification =>
                                      values['personType'] === 'Civilian'
                                        ? qualification !== agentQualification
                                        : true
                                  );

                                  return qualifications?.map(qualification => ({
                                    label: qualification.name,
                                    value: qualification.id,
                                    type: qualification.type
                                  }));
                                }
                              })}
                              placeholder={qualificationsRequestStatus.maybeMap(
                                {
                                  idle: () => 'Selecione',
                                  loading: () => 'Carregando...',
                                  failed: () =>
                                    'Erro. Clique no botão acima para carregar as opções!',
                                  succeeded: () => 'Selecione'
                                }
                              )}
                              onChange={options =>
                                onChangeQualificationField(options)
                              }
                              value={values[field.name]}
                              isDisabled={false}
                              isMulti={true}
                              isInvalid={isInvalid(field.name)}
                              fieldName="qualificações"
                            />
                          )}
                        />
                      </Col>
                    </Row>
                  </>
                }
              />
              <Row>
                <Col md={3}>
                  <FormItem
                    name="name"
                    label="Nome/Apelido da vítima"
                    component={field => (
                      <Form.Control
                        {...field}
                        data-cy={dataCy(field.name, index)}
                        type="text"
                        placeholder="Digite o nome da vítima..."
                        isInvalid={isInvalid(field.name)}
                        onChange={(e: any) =>
                          handleChangeSilent(field.name, e.target.value)
                        }
                        onBlur={handleBlur}
                      />
                    )}
                  />
                </Col>
                <Col md={1}>
                  <FormItem
                    name="age"
                    label="Idade"
                    component={field => (
                      <Form.Control
                        {...field}
                        data-cy={dataCy(field.name, index)}
                        type="number"
                        placeholder="Digite..."
                        isInvalid={isInvalid(field.name)}
                        onChange={(e: any) => onChangeAgeField(e)}
                        onBlur={(e: any) => {
                          handleBlur(e);
                          syncField('ageGroupId');
                        }}
                      />
                    )}
                  />
                </Col>
                <Col md={2}>
                  <FormItem
                    name="ageGroupId"
                    label="Faixa Etária*"
                    failRequest={ageGroupsRequestStatus.maybeMap({
                      failed: () => true
                    })}
                    onPressedRefresh={() =>
                      createEditOccurrenceUsecase.getAgeGroups()
                    }
                    component={field => (
                      <Form.Select
                        name={field.name}
                        data-cy={dataCy(field.name, index)}
                        value={values[field.name]}
                        onChange={(e: any) =>
                          handleChange(field.name, e.target.value)
                        }
                        onBlur={handleBlur}
                        isInvalid={isInvalid(field.name)}
                      >
                        {ageGroupsRequestStatus.maybeMap({
                          idle: () => (
                            <option>Carregando faixa etária...</option>
                          ),
                          loading: () => <option>Carregando...</option>,
                          failed: () => (
                            <option>
                              Erro. Clique no botão acima para carregar as
                              opções!
                            </option>
                          ),
                          succeeded(data) {
                            return (
                              <>
                                <option value="">Selecione</option>
                                {data?.map(agegroup => (
                                  <option value={agegroup.id}>
                                    {agegroup.name}
                                  </option>
                                ))}
                              </>
                            );
                          }
                        })}
                      </Form.Select>
                    )}
                  />
                </Col>
                <Col md={2}>
                  <FormItem
                    name="genreId"
                    label="Gênero*"
                    failRequest={genresRequestStatus.maybeMap({
                      failed: () => true
                    })}
                    onPressedRefresh={() =>
                      createEditOccurrenceUsecase.getGenres()
                    }
                    component={field => (
                      <Form.Select
                        name={field.name}
                        data-cy={dataCy(field.name, index)}
                        value={values[field.name]}
                        onChange={(e: any) =>
                          handleChange(field.name, e.target.value)
                        }
                        onBlur={handleBlur}
                        isInvalid={isInvalid(field.name)}
                      >
                        {genresRequestStatus.maybeMap({
                          idle: () => <option>Carregando...</option>,
                          loading: () => <option>Carregando...</option>,
                          failed: () => (
                            <option>
                              Erro. Clique no botão acima para carregar as
                              opções!
                            </option>
                          ),
                          succeeded(data) {
                            return (
                              <>
                                <option value="">Selecione</option>
                                {data?.map(genre => (
                                  <option value={genre.id}>{genre.name}</option>
                                ))}
                              </>
                            );
                          }
                        })}
                      </Form.Select>
                    )}
                  />
                </Col>
                <Col md={2}>
                  <FormItem
                    name="race"
                    label="Raça*"
                    failRequest={racesRequestStatus.maybeMap({
                      failed: () => true
                    })}
                    onPressedRefresh={() =>
                      createEditOccurrenceUsecase.getRaces()
                    }
                    component={field => (
                      <Form.Select
                        name={field.name}
                        data-cy={dataCy(field.name, index)}
                        value={values[field.name]}
                        onChange={(e: any) =>
                          handleChange(field.name, e.target.value)
                        }
                        onBlur={handleBlur}
                        isInvalid={isInvalid(field.name)}
                      >
                        {racesRequestStatus.maybeMap({
                          idle: () => <option>Carregando...</option>,
                          loading: () => <option>Carregando...</option>,
                          failed: () => (
                            <option>
                              Erro. Clique no botão acima para carregar as
                              opções!
                            </option>
                          ),
                          succeeded(data) {
                            return (
                              <>
                                <option value="">Selecione</option>
                                {data.raceList.map(race => (
                                  <option value={race}>{race}</option>
                                ))}
                              </>
                            );
                          }
                        })}
                      </Form.Select>
                    )}
                  />
                </Col>
                <Col md={2}>
                  <FormItem
                    name="placeId"
                    label="Local*"
                    failRequest={placesRequestStatus.maybeMap({
                      failed: () => true
                    })}
                    onPressedRefresh={() =>
                      createEditOccurrenceUsecase.getPlaces()
                    }
                    component={field => (
                      <Form.Select
                        name={field.name}
                        data-cy={dataCy(field.name, index)}
                        value={values[field.name]}
                        onChange={(e: any) =>
                          handleChange(field.name, e.target.value)
                        }
                        onBlur={handleBlur}
                        isInvalid={isInvalid(field.name)}
                      >
                        {placesRequestStatus.maybeMap({
                          idle: () => <option>Carregando...</option>,
                          loading: () => <option>Carregando...</option>,
                          failed: () => (
                            <option>
                              Erro. Clique no botão acima para carregar as
                              opções!
                            </option>
                          ),
                          succeeded(data) {
                            return (
                              <>
                                <option value="">Selecione</option>
                                {data?.map(place => (
                                  <option value={place.id}>{place.name}</option>
                                ))}
                              </>
                            );
                          }
                        })}
                      </Form.Select>
                    )}
                  />
                </Col>
              </Row>
              <Divider height={24} />
              <Row>
                <Col>
                  <FormItem
                    name="circumstances"
                    label="Circunstâncias*"
                    failRequest={circumstancesRequestStatus.maybeMap({
                      failed: () => true
                    })}
                    onPressedRefresh={() =>
                      createEditOccurrenceUsecase.getCircumstances()
                    }
                    component={field => (
                      <RefactorSelectWithSearch
                        dataCy={dataCy(field.name, index)}
                        options={circumstancesRequestStatus.maybeMap({
                          idle: () => [],
                          loading: () => [],
                          failed: () => [],
                          succeeded(data) {
                            return data
                              ?.filter(
                                circumstance =>
                                  circumstance.type !== CircumstanceType.ANIMAL
                              )
                              .map(circumstance => ({
                                label: circumstance.name,
                                value: circumstance.id
                              }));
                          }
                        })}
                        placeholder={circumstancesRequestStatus.maybeMap({
                          idle: () => 'Selecione',
                          loading: () => 'Carregando...',
                          failed: () =>
                            'Erro. Clique no botão acima para carregar as opções!',
                          succeeded: () => 'Selecione'
                        })}
                        onChange={options =>
                          handleChange(
                            field.name,
                            options.map((option: any) => option.value)
                          )
                        }
                        value={values[field.name]}
                        isDisabled={false}
                        isMulti={true}
                        isInvalid={isInvalid(field.name)}
                        fieldName="circunstâncias"
                      />
                    )}
                  />
                </Col>

                <Col>
                  <FormItem
                    name="generalObservation"
                    label="Observações"
                    component={field => (
                      <Form.Control
                        {...field}
                        data-cy={dataCy(field.name, index)}
                        type="text"
                        placeholder="Digite as observações..."
                        isInvalid={isInvalid(field.name)}
                        onChange={(e: any) =>
                          handleChangeSilent(field.name, e.target.value)
                        }
                        onBlur={handleBlur}
                      />
                    )}
                  />
                </Col>
                {enableFieldsByTypeQualification(
                  values['qualifications'],
                  QualificationType.SERVICE
                ) && (
                  <Col>
                    <FormItem
                      name="serviceStatusId"
                      label="Status do serviço*"
                      failRequest={statusRequestStatus.maybeMap({
                        failed: () => true
                      })}
                      onPressedRefresh={() =>
                        createEditOccurrenceUsecase.getStatus()
                      }
                      component={field => (
                        <Form.Select
                          name={field.name}
                          data-cy={dataCy(field.name, index)}
                          value={values[field.name]}
                          onChange={(e: any) =>
                            handleChange(field.name, e.target.value)
                          }
                          onBlur={handleBlur}
                          isInvalid={isInvalid(field.name)}
                        >
                          {statusRequestStatus.maybeMap({
                            idle: () => <option>Carregando...</option>,
                            loading: () => <option>Carregando...</option>,
                            failed: () => (
                              <option>
                                Erro. Clique no botão acima para carregar as
                                opções!
                              </option>
                            ),
                            succeeded(data) {
                              return (
                                <>
                                  <option value="">Selecione</option>
                                  {data?.map(
                                    status =>
                                      status.type === StatusType.SERVICE && (
                                        <option
                                          key={status.id}
                                          value={status.id}
                                        >
                                          {status.name}
                                        </option>
                                      )
                                  )}
                                </>
                              );
                            }
                          })}
                        </Form.Select>
                      )}
                    />
                  </Col>
                )}
              </Row>
              {enableFieldsByTypeQualification(
                values['qualifications'],
                QualificationType.POLITICAL
              ) && (
                <>
                  <Divider height={24} />
                  <Row>
                    <Col>
                      <FormItem
                        name="partieId"
                        label="Partido político*"
                        failRequest={partiesRequestStatus.maybeMap({
                          failed: () => true
                        })}
                        onPressedRefresh={() =>
                          createEditOccurrenceUsecase.getParties()
                        }
                        component={field => (
                          <Form.Select
                            name={field.name}
                            data-cy={dataCy(field.name, index)}
                            value={values[field.name]}
                            onChange={(e: any) =>
                              handleChange(field.name, e.target.value)
                            }
                            onBlur={handleBlur}
                            isInvalid={isInvalid(field.name)}
                          >
                            {partiesRequestStatus.maybeMap({
                              idle: () => <option>Carregando...</option>,
                              loading: () => <option>Carregando...</option>,
                              failed: () => (
                                <option>
                                  Erro. Clique no botão acima para carregar as
                                  opções!
                                </option>
                              ),
                              succeeded(data) {
                                return (
                                  <>
                                    <option value="">Selecione</option>
                                    {data?.map(partie => (
                                      <option value={partie.id}>
                                        {partie.name}
                                      </option>
                                    ))}
                                  </>
                                );
                              }
                            })}
                          </Form.Select>
                        )}
                      />
                    </Col>
                    <Col>
                      <FormItem
                        name="politicalPositionId"
                        label="Cargo*"
                        failRequest={positionsRequestStatus.maybeMap({
                          failed: () => true
                        })}
                        onPressedRefresh={() =>
                          createEditOccurrenceUsecase.getPositions()
                        }
                        component={field => (
                          <Form.Select
                            name={field.name}
                            data-cy={dataCy(field.name, index)}
                            value={values[field.name]}
                            onChange={(e: any) =>
                              handleChange(field.name, e.target.value)
                            }
                            onBlur={handleBlur}
                            isInvalid={isInvalid(field.name)}
                          >
                            {positionsRequestStatus.maybeMap({
                              idle: () => <option>Carregando...</option>,
                              loading: () => <option>Carregando...</option>,
                              failed: () => (
                                <option>
                                  Erro. Clique no botão acima para carregar as
                                  opções!
                                </option>
                              ),
                              succeeded(data) {
                                return (
                                  <>
                                    <option value="">Selecione</option>
                                    {data?.map(
                                      position =>
                                        position.type ===
                                          PositionType.POLITICAL && (
                                          <option
                                            key={position.id}
                                            value={position.id}
                                          >
                                            {position.name}
                                          </option>
                                        )
                                    )}
                                  </>
                                );
                              }
                            })}
                          </Form.Select>
                        )}
                      />
                    </Col>
                    <Col>
                      <FormItem
                        name="politicalStatusId"
                        label="Status do político*"
                        failRequest={statusRequestStatus.maybeMap({
                          failed: () => true
                        })}
                        onPressedRefresh={() =>
                          createEditOccurrenceUsecase.getStatus()
                        }
                        component={field => (
                          <Form.Select
                            name={field.name}
                            data-cy={dataCy(field.name, index)}
                            value={values[field.name]}
                            onChange={(e: any) =>
                              handleChange(field.name, e.target.value)
                            }
                            onBlur={handleBlur}
                            isInvalid={isInvalid(field.name)}
                          >
                            {statusRequestStatus.maybeMap({
                              idle: () => <option>Carregando...</option>,
                              loading: () => <option>Carregando...</option>,
                              failed: () => (
                                <option>
                                  Erro. Clique no botão acima para carregar as
                                  opções!
                                </option>
                              ),
                              succeeded(data) {
                                return (
                                  <>
                                    <option value="">Selecione</option>
                                    {data?.map(
                                      status =>
                                        status.type ===
                                          StatusType.POLITICAL && (
                                          <option
                                            key={status.id}
                                            value={status.id}
                                          >
                                            {status.name}
                                          </option>
                                        )
                                    )}
                                  </>
                                );
                              }
                            })}
                          </Form.Select>
                        )}
                      />
                    </Col>
                  </Row>
                </>
              )}
              {enableFieldsByTypeQualification(
                values['qualifications'],
                QualificationType.AGENT
              ) && (
                <>
                  <Divider height={24} />

                  <Row>
                    <Col>
                      <FormItem
                        name="coorporationId"
                        label="Corporação do agente*"
                        failRequest={coorporationsRequestStatus.maybeMap({
                          failed: () => true
                        })}
                        onPressedRefresh={() =>
                          createEditOccurrenceUsecase.getCoorporations()
                        }
                        component={field => (
                          <Form.Select
                            name={field.name}
                            data-cy={dataCy(field.name, index)}
                            value={values[field.name]}
                            onChange={(e: any) =>
                              handleChange(field.name, e.target.value)
                            }
                            onBlur={handleBlur}
                            isInvalid={isInvalid(field.name)}
                          >
                            {coorporationsRequestStatus.maybeMap({
                              idle: () => <option>Carregando...</option>,
                              loading: () => <option>Carregando...</option>,
                              failed: () => (
                                <option>
                                  Erro. Clique no botão acima para carregar as
                                  opções!
                                </option>
                              ),
                              succeeded(data) {
                                return (
                                  <>
                                    <option value="">Selecione</option>
                                    {data?.map(coorporation => (
                                      <option value={coorporation.id}>
                                        {coorporation.name}
                                      </option>
                                    ))}
                                  </>
                                );
                              }
                            })}
                          </Form.Select>
                        )}
                      />
                    </Col>
                    <Col>
                      <FormItem
                        name="agentPositionId"
                        label="Posição do agente*"
                        failRequest={positionsRequestStatus.maybeMap({
                          failed: () => true
                        })}
                        onPressedRefresh={() =>
                          createEditOccurrenceUsecase.getPositions()
                        }
                        component={field => (
                          <Form.Select
                            name={field.name}
                            data-cy={dataCy(field.name, index)}
                            value={values[field.name]}
                            onChange={(e: any) =>
                              handleChange(field.name, e.target.value)
                            }
                            onBlur={handleBlur}
                            isInvalid={isInvalid(field.name)}
                          >
                            {positionsRequestStatus.maybeMap({
                              idle: () => <option>Carregando...</option>,
                              loading: () => <option>Carregando...</option>,
                              failed: () => (
                                <option>
                                  Erro. Clique no botão acima para carregar as
                                  opções!
                                </option>
                              ),
                              succeeded(data) {
                                return (
                                  <>
                                    <option value="">Selecione</option>
                                    {data?.map(
                                      position =>
                                        position.type ===
                                          PositionType.AGENT && (
                                          <option
                                            key={position.id}
                                            value={position.id}
                                          >
                                            {position.name}
                                          </option>
                                        )
                                    )}
                                  </>
                                );
                              }
                            })}
                          </Form.Select>
                        )}
                      />
                    </Col>
                    <Col>
                      <FormItem
                        name="agentStatusId"
                        label="Status do agente*"
                        failRequest={statusRequestStatus.maybeMap({
                          failed: () => true
                        })}
                        onPressedRefresh={() =>
                          createEditOccurrenceUsecase.getStatus()
                        }
                        component={field => (
                          <Form.Select
                            name={field.name}
                            data-cy={dataCy(field.name, index)}
                            value={values[field.name]}
                            onChange={(e: any) =>
                              handleChange(field.name, e.target.value)
                            }
                            onBlur={handleBlur}
                            isInvalid={isInvalid(field.name)}
                          >
                            {statusRequestStatus.maybeMap({
                              idle: () => <option>Carregando...</option>,
                              loading: () => <option>Carregando...</option>,
                              failed: () => (
                                <option>
                                  Erro. Clique no botão acima para carregar as
                                  opções!
                                </option>
                              ),
                              succeeded(data) {
                                return (
                                  <>
                                    <option value="">Selecione</option>
                                    {data?.map(
                                      status =>
                                        status.type === StatusType.AGENT && (
                                          <option
                                            key={status.id}
                                            value={status.id}
                                          >
                                            {status.name}
                                          </option>
                                        )
                                    )}
                                  </>
                                );
                              }
                            })}
                          </Form.Select>
                        )}
                      />
                    </Col>
                  </Row>

                  <Divider height={24} />

                  <Col>
                    <FormItem
                      name="unit"
                      label="Unidade do agente"
                      component={field => (
                        <Form.Control
                          {...field}
                          data-cy={dataCy(field.name, index)}
                          type="text"
                          placeholder="Digite a unidade do agente..."
                          isInvalid={isInvalid(field.name)}
                          onChange={(e: any) =>
                            handleChangeSilent(field.name, e.target.value)
                          }
                          onBlur={handleBlur}
                        />
                      )}
                    />
                  </Col>
                </>
              )}
              {values['situation'].toLowerCase() === 'dead' && (
                <>
                  <Divider height={24} />
                  <Col md={2}>
                    <FormItem
                      name="deathDate"
                      label="Data da morte"
                      component={field => (
                        <Field
                          as="input"
                          type="datetime-local"
                          name={field.name}
                          data-cy={dataCy(field.name, index)}
                          value={values[field.name]}
                          max={maxDateTime}
                          onChange={(e: any) =>
                            handleChange(field.name, e.target.value)
                          }
                          onBlur={handleBlur}
                          className={
                            isInvalid(field.name)
                              ? 'form-control is-invalid'
                              : 'form-control'
                          }
                          disabled={
                            values['situation'].toLowerCase() !== 'dead'
                          }
                        />
                      )}
                    />
                  </Col>
                </>
              )}
            </Form>
          );
        }}
      />
    </div>
  )
);

const CreateEditOcurrenceVictimsFormComponent: React.FC = () => {
  const { createEditOccurrenceUsecase, createEditOccurrenceState } =
    useContext(OccurrenceContext);

  const maxDateTime = useMemo(() => convertToDateTimeInput(new Date()), []);

  const enableFieldsByTypeQualification = useCallback(
    (idsQualification: string[], type: QualificationType) =>
      createEditOccurrenceState.qualificationsRequestStatus.maybeMap({
        succeeded(data) {
          return idsQualification.some(id => {
            const qualification = data.find(data => data.id === id);
            return qualification?.type === type;
          });
        },
        failed: () => false
      }) || false,
    [createEditOccurrenceState.qualificationsRequestStatus]
  );

  const dataCy = useCallback((field: string, index?: number) => {
    let prefix = 'victimForm';

    if (index !== null && index !== undefined) {
      return `${prefix}-${index}-${field}`;
    }
    return `${prefix}-${field}`;
  }, []);

  return (
    <FormBody
      title="Vítimas"
      description="Complementação com informações sobre pessoas baleadas em cada registro (apenas administradores)."
    >
      <Row>
        <Col md={2}>
          <AddItemOnFormButton
            name="Incluir vítima"
            dataCy={dataCy('addVictimButton')}
            onClick={() => createEditOccurrenceUsecase.addNewVictimsForm()}
          />
        </Col>
        <Col md={2}>
          <FormItemCount
            label="Total de vítimas"
            value={createEditOccurrenceState.victimsCount.total}
          />
        </Col>
        <Col md={2}>
          <FormItemCount
            label="Civis mortos"
            value={createEditOccurrenceState.victimsCount.civilDead}
          />
        </Col>
        <Col md={2}>
          <FormItemCount
            label="Civis feridos"
            value={createEditOccurrenceState.victimsCount.civilWounded}
          />
        </Col>
        <Col md={2}>
          <FormItemCount
            label="Agentes mortos"
            value={createEditOccurrenceState.victimsCount.agentDead}
          />
        </Col>
        <Col md={2}>
          <FormItemCount
            label="Agentes feridos"
            value={createEditOccurrenceState.victimsCount.agentWounded}
          />
        </Col>
      </Row>

      {createEditOccurrenceState.victimsForm.map((victimForm, index) => (
        <VictimFormItem
          key={victimForm.uniqueKey}
          victimForm={victimForm}
          index={index}
          maxDateTime={maxDateTime}
          dataCy={dataCy}
          enableFieldsByTypeQualification={enableFieldsByTypeQualification}
          createEditOccurrenceUsecase={createEditOccurrenceUsecase}
          fieldsForValidation={createEditOccurrenceState.fieldsForValidation}
          agentQualification={createEditOccurrenceState.agentQualification}
          qualificationsRequestStatus={
            createEditOccurrenceState.qualificationsRequestStatus
          }
          ageGroupsRequestStatus={
            createEditOccurrenceState.ageGroupsRequestStatus
          }
          genresRequestStatus={createEditOccurrenceState.genresRequestStatus}
          racesRequestStatus={createEditOccurrenceState.racesRequestStatus}
          placesRequestStatus={createEditOccurrenceState.placesRequestStatus}
          circumstancesRequestStatus={
            createEditOccurrenceState.circumstancesRequestStatus
          }
          statusRequestStatus={createEditOccurrenceState.statusRequestStatus}
          partiesRequestStatus={createEditOccurrenceState.partiesRequestStatus}
          positionsRequestStatus={
            createEditOccurrenceState.positionsRequestStatus
          }
          coorporationsRequestStatus={
            createEditOccurrenceState.coorporationsRequestStatus
          }
        />
      ))}
      <Divider height={40} />
      <h2 style={{ fontSize: '11px' }}>
        * - Campo é obrigatório. Em caso de informação faltante, preencher com
        "Não identificado" ou "Não informado".
      </h2>
    </FormBody>
  );
};

export default CreateEditOcurrenceVictimsFormComponent;
